import { partialPayload, pipeReducers } from '@ardoq/common-helpers';
import { MetamodelViewGraphEdge, MetaModelViewGraphNode } from '@ardoq/graph';
import { uniq } from 'lodash';
import {
  GraphId,
  InstanceCounts,
  PathCollapsingRuleInternal,
  SelectedGraph,
  TraversalBuilderState,
} from '../types';
import { requiredComponentsOperations } from '../requiredComponentsTab/requiredComponentsOperations';
import { getGraphEdgeToReferenceMapEntry } from './addNodeAndEdge';
import {
  updateEdgesToBeRemovedOnCollapsing,
  updatePathCollapsingRules,
} from './pathCollapsing';
import { removeEdgesForGraphNodeId } from './removeEdges';
import { setTriplesOptions } from './setTraversal';
import { toConnectedSubgraph } from './toConnectedSubgraph';
import { toggleIsSelectedGraphNodeOrEdge } from './toggleSelection';
import { updateFilters } from './updateFilters';
import { updateSelectedGraph } from './updateSelectedGraph';

const partitionMap = <K, V>(
  map: Map<K, V>,
  filter: ([key, value]: [key: K, value: V]) => boolean
): [Map<K, V>, Map<K, V>] => {
  const map1 = new Map<K, V>();
  const map2 = new Map<K, V>();
  map.forEach((value, key) => {
    if (filter([key, value])) {
      map1.set(key, value);
    } else {
      map2.set(key, value);
    }
  });
  return [map1, map2];
};

type RebuildSelectedGraphArgs = {
  startNodeId: string;
  referenceMap: Map<
    string,
    {
      sourceId: GraphId;
      targetId: GraphId;
      tripleId: string;
    }
  >;
  graphNodes: Map<string, MetaModelViewGraphNode>;
  graphEdges: Map<string, MetamodelViewGraphEdge>;
  pathCollapsingRules: PathCollapsingRuleInternal[];
};

const rebuildSelectedGraph = ({
  startNodeId,
  referenceMap,
  graphNodes,
  graphEdges,
  pathCollapsingRules,
}: RebuildSelectedGraphArgs): {
  selectedGraph: SelectedGraph;
  graphNodes: Map<string, MetaModelViewGraphNode>;
  graphEdges: Map<string, MetamodelViewGraphEdge>;
  removedNodes: Map<string, MetaModelViewGraphNode>;
  removedEdges: Map<string, MetamodelViewGraphEdge>;
} => {
  const virtualEdgeStates = pathCollapsingRules.flatMap(
    rule => rule.virtualEdgeStates
  );

  const collapsedEdges = uniq(
    virtualEdgeStates.flatMap(({ collapsedEdges }) => collapsedEdges)
  ).map(id => graphEdges.get(id)!);

  // If a collapsed path has branches they will be shown in the viewpoint
  // builder graph, that means edges of a collapsed path will be shown in the
  // graph. The user can remove them by toggling the according triple option.
  // That means we need to ensure that all edges of all collapsed paths are
  // in the reference map, the source to build the selected graph, when we
  // rebuild the graph.
  collapsedEdges.forEach(
    ({ id: referenceId, source: sourceId, target: targetId, modelId }) => {
      if (referenceMap.has(referenceId)) {
        return;
      }
      referenceMap.set(referenceId, {
        sourceId,
        targetId,
        tripleId: modelId,
      });
    }
  );

  const selectedGraph = updateSelectedGraph(
    toConnectedSubgraph(startNodeId, referenceMap)
  );

  const allGraphNodesIds = new Set([
    ...Array.from(selectedGraph.sourceMap.keys()),
    ...Array.from(selectedGraph.targetMap.keys()),
  ]);
  const allGraphEdgeIds = new Set(selectedGraph.referenceMap.keys());

  const [updatedGraphNodes, removedNodes] = partitionMap(
    graphNodes,
    ([key]) => allGraphNodesIds.has(key) || key === startNodeId
  );
  const [updateGraphEdges, removedEdges] = partitionMap(graphEdges, ([key]) =>
    allGraphEdgeIds.has(key)
  );

  return {
    selectedGraph,
    removedNodes,
    removedEdges,
    graphNodes: updatedGraphNodes,
    graphEdges: updateGraphEdges,
  };
};

export const rebuildSelectedGraphAndAccordingState = ({
  graphNodes,
  graphEdges,
  state,
  referenceMap,
  instanceCounts,
}: {
  graphNodes: Map<string, MetaModelViewGraphNode>;
  graphEdges: Map<string, MetamodelViewGraphEdge>;
  state: TraversalBuilderState;
  referenceMap: Map<
    string,
    { sourceId: GraphId; targetId: GraphId; tripleId: string }
  >;
  instanceCounts: Map<string, InstanceCounts>;
}) => {
  const {
    selectedGraph,
    removedNodes,
    graphNodes: updatedGraphNodes,
    graphEdges: updatedGraphEdges,
  } = rebuildSelectedGraph({
    startNodeId: state.startNode!.id,
    referenceMap: referenceMap,
    graphNodes,
    graphEdges,
    pathCollapsingRules: state.pathCollapsing.rules,
  });

  return pipeReducers(
    {
      ...state,
      graphNodes: updatedGraphNodes,
      graphEdges: updatedGraphEdges,
      selectedGraph,
      instanceCounts,
    },
    partialPayload(
      requiredComponentsOperations.removeRequiredNodes,
      new Set(removedNodes.keys())
    ),
    partialPayload(updatePathCollapsingRules, state.pathCollapsing.rules),
    updateEdgesToBeRemovedOnCollapsing,
    partialPayload(
      setTriplesOptions,
      state.tripleOptions.instanceCountsResponse
    ),
    updateFilters
  );
};

export const removeNodeFromGraph = (
  state: TraversalBuilderState,
  graphNodeId: string
): TraversalBuilderState => {
  const { graphNodes, graphEdges } = state;
  const referenceMapWithRemovedEdges = removeEdgesForGraphNodeId({
    graphNodeId,
    selectedGraph: state.selectedGraph,
    referenceMap: state.selectedGraph.referenceMap,
  });

  const {
    selectedGraph,
    removedNodes,
    graphNodes: updatedGraphNodes,
    graphEdges: updatedGraphEdges,
  } = rebuildSelectedGraph({
    startNodeId: state.startNode!.id,
    referenceMap: referenceMapWithRemovedEdges,
    graphNodes,
    graphEdges,
    pathCollapsingRules: state.pathCollapsing.rules,
  });

  const stateWithoutRemovedGraphNode = pipeReducers(
    {
      ...state,
      graphNodes: updatedGraphNodes,
      graphEdges: updatedGraphEdges,
      selectedGraph,
    },
    partialPayload(
      requiredComponentsOperations.removeRequiredNodes,
      new Set(removedNodes.keys())
    ),
    partialPayload(updatePathCollapsingRules, state.pathCollapsing.rules),
    updateEdgesToBeRemovedOnCollapsing
  );

  if (graphNodeId !== state.selectedGraphNodeId) {
    return updateFilters(stateWithoutRemovedGraphNode);
  }

  return updateFilters(
    toggleIsSelectedGraphNodeOrEdge(stateWithoutRemovedGraphNode, {
      graphNodeId,
      type: 'graphNode',
    })
  );
};

export const buildCollapsedGraph = (
  state: TraversalBuilderState
): {
  collapsedGraphNodes: Map<string, MetaModelViewGraphNode>;
  collapsedGraphEdges: Map<string, MetamodelViewGraphEdge>;
  collapsedSelectedGraph: SelectedGraph;
} => {
  const {
    pathCollapsing: { rules },
    startNode,
  } = state;
  const activeRules = rules.filter(rule => rule.isActive && !rule.isExpanded);
  if (!startNode || activeRules.length === 0)
    return {
      collapsedGraphNodes: state.graphNodes,
      collapsedGraphEdges: state.graphEdges,
      collapsedSelectedGraph: state.selectedGraph,
    };
  const collapsedGraphNodesCandidates = new Map([...state.graphNodes]);
  const collapsedGraphEdgesCandidates = new Map([...state.graphEdges]);
  const referenceMap = new Map(state.selectedGraph.referenceMap);
  const graphEdgeToReferenceMapEntry = getGraphEdgeToReferenceMapEntry(
    collapsedGraphNodesCandidates
  );
  activeRules.forEach(rule => {
    rule.virtualEdgeStates.forEach(
      ({ virtualEdge, edgesToBeRemovedOnCollapsing }) => {
        collapsedGraphEdgesCandidates.set(virtualEdge.id, virtualEdge);
        referenceMap.set(
          virtualEdge.id,
          graphEdgeToReferenceMapEntry(virtualEdge)
        );
        edgesToBeRemovedOnCollapsing.forEach(id => referenceMap.delete(id));
      }
    );
  });
  const {
    selectedGraph: collapsedSelectedGraph,
    graphNodes: collapsedGraphNodes,
    graphEdges: collapsedGraphEdges,
  } = rebuildSelectedGraph({
    startNodeId: startNode.id,
    referenceMap,
    graphNodes: collapsedGraphNodesCandidates,
    graphEdges: collapsedGraphEdgesCandidates,
    pathCollapsingRules: [] as PathCollapsingRuleInternal[],
  });

  return {
    collapsedGraphNodes,
    collapsedGraphEdges,
    collapsedSelectedGraph,
  };
};
